import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "./button";
import { NAVIGATION_ITEMS } from "../../lib/constants";
import { cn } from "../../lib/utils";
import { navigationClick } from "../../lib/analytics-factory";

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const toggleSubMenu = (name: string) => {
    setOpenSubMenu(openSubMenu === name ? null : name);
  };

  // Helper function to check if an item or any of its children are active
  const isItemActive = (item: typeof NAVIGATION_ITEMS[0]): boolean => {
    // Direct match for the current path
    if (location.pathname === item.href) {
      return true;
    }
    
    // Check if any children match the current path
    if (item.children && item.children.length > 0) {
      return item.children.some(child => location.pathname === child.href);
    }
    
    return false;
  };

  // Set the active parent menu to be expanded when the menu opens
  useEffect(() => {
    if (isOpen) {
      // Find which parent menu item contains the current location as a child
      const activeParent = NAVIGATION_ITEMS.find(item => 
        item.children && 
        item.children.length > 0 && 
        item.children.some(child => location.pathname === child.href)
      );
      
      if (activeParent) {
        setOpenSubMenu(activeParent.name);
      }
    }
  }, [isOpen, location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) && 
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node) && 
        isOpen
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMenuToggle = () => {
    // Track only when opening the menu
    if (!isOpen) {
      const event = {} as React.MouseEvent<HTMLElement>;
      navigationClick("mobile_menu_open")(event);
    }
    setIsOpen(!isOpen);
  };

  // Render the menu button
  const mobileMenuButton = (
    <div className="md:hidden" ref={buttonRef}>
      <Button
        variant="ghost"
        className="h-9 w-9 p-0"
        onClick={handleMenuToggle}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>
    </div>
  );

  // Render the mobile menu using Portal so it's outside the header container
  const mobileMenu = isOpen && createPortal(
    <div className="md:hidden" style={{ position: 'relative', zIndex: 9999 }}>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
      <div ref={menuRef} className="fixed inset-y-0 right-0 w-3/4 max-w-sm bg-background shadow-lg p-6 overflow-y-auto">
        <nav className="flex flex-col gap-6">
          {NAVIGATION_ITEMS.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isActive = isItemActive(item);
            
            return (
              <div key={item.name} className="relative">
                {hasChildren ? (
                  <>
                    <button 
                      className={cn(
                        "flex w-full items-center justify-between text-base font-medium transition-colors hover:text-primary",
                        isActive ? "text-primary" : "text-foreground"
                      )}
                      onClick={() => toggleSubMenu(item.name)}
                    >
                      {item.name}
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className={cn(
                          "h-4 w-4 transition-transform", 
                          openSubMenu === item.name ? "rotate-180" : ""
                        )}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    
                    {openSubMenu === item.name && (
                      <div className="mt-2 pl-4 border-l border-primary/10">
                        {item.children && item.children.map((child) => (
                          <React.Fragment key={child.name}>
                            {child.separator && (
                              <div className="my-2 border-t border-border" />
                            )}
                            <Link
                              to={child.href}
                              className={cn(
                                "block py-2 text-sm transition-colors hover:text-primary",
                                location.pathname === child.href ? "text-primary" : "text-muted-foreground"
                              )}
                              onClick={() => {
                                setIsOpen(false);
                                const event = {} as React.MouseEvent<HTMLElement>;
                                navigationClick(`mobile_nav_${item.name.toLowerCase().replace(/\s+/g, '_')}_${child.name.toLowerCase().replace(/\s+/g, '_')}`)(event);
                              }}
                            >
                              {child.name}
                            </Link>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      "block text-base font-medium transition-colors hover:text-primary",
                      isActive ? "text-primary" : "text-foreground"
                    )}
                    onClick={() => {
                      setIsOpen(false);
                      const event = {} as React.MouseEvent<HTMLElement>;
                      navigationClick(`mobile_nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`)(event);
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            );
          })}
          
          <div className="border-t border-border pt-6 mt-2">
            <Link
              to="/contact"
              className={cn(
                "block text-base font-medium transition-colors hover:text-primary",
                location.pathname === "/contact" ? "text-primary" : "text-foreground"
              )}
              onClick={() => {
                setIsOpen(false);
                const event = {} as React.MouseEvent<HTMLElement>;
                navigationClick("mobile_nav_contact_us")(event);
              }}
            >
              Contact Us
            </Link>
          </div>
        </nav>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {mobileMenuButton}
      {mobileMenu}
    </>
  );
}